import { CircleSolid, Company, Share } from "@ignite-analytics/icons";
import { Button, Card, CardContent, Chip, Grid, ListItem, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { FormattedMessage } from "react-intl";
import { Link, useSearchParams } from "react-router-dom";

import { Assessment } from "../../globals/types";
import { basePath } from "../ApplicationPage/constants";

function getFeedbackAssessmentStatus(assessment: Assessment): "Submitted" | "InProgress" | "NotStarted" {
    if (assessment.submittedAt) {
        return "Submitted";
    }
    if (assessment.answeredQuestionIds.length > 0) {
        return "InProgress";
    }
    return "NotStarted";
}

interface AssessmentListItemProps {
    assessment: Assessment;
}

export const AssessmentListItem: React.FC<AssessmentListItemProps> = ({ assessment }) => {
    const status = getFeedbackAssessmentStatus(assessment);
    const [searchParams] = useSearchParams();

    // Scope chips: shown only when the initiator picked a BU and/or category.
    // Same color tokens + icons as the questionnaire header so a responder
    // who's seen one recognises the other immediately.
    const businessUnitName = assessment.evaluationScope?.businessUnitName ?? null;
    const spendCategoryName = assessment.evaluationScope?.spendCategoryName ?? null;
    const showScope = !!(businessUnitName || spendCategoryName);

    let chip: React.ReactNode;
    if (assessment.expired && status !== "Submitted") {
        chip = (
            <Chip
                label={<FormattedMessage defaultMessage="Expired" />}
                color="neutral"
                variant="status"
                icon={<CircleSolid />}
                iconSize="small"
            />
        );
    } else {
        switch (status) {
            case "Submitted": {
                chip = (
                    <Chip
                        label={<FormattedMessage defaultMessage="Submitted" />}
                        color="success"
                        variant="status"
                        icon={<CircleSolid />}
                        iconSize="small"
                    />
                );
                break;
            }
            case "InProgress": {
                chip = (
                    <Chip
                        label={<FormattedMessage defaultMessage="In progress" />}
                        color="warning"
                        variant="status"
                        icon={<CircleSolid />}
                        iconSize="small"
                    />
                );
                break;
            }
            case "NotStarted": {
                chip = (
                    <Chip
                        label={<FormattedMessage defaultMessage="Not started" />}
                        color="neutral"
                        variant="status"
                        icon={<CircleSolid />}
                        iconSize="small"
                    />
                );
                break;
            }
        }
    }

    let dateLine: React.ReactNode;
    if (status === "Submitted" && assessment.submittedAt) {
        dateLine = (
            <Typography variant="textSm" color="textTextHelper">
                <FormattedMessage
                    defaultMessage="Submitted: {date}"
                    values={{ date: dayjs(assessment.submittedAt).format("DD MMM YYYY") }}
                />
            </Typography>
        );
    } else if (status !== "Submitted" && assessment.sentAt) {
        dateLine = (
            <Typography variant="textSm" color="textTextHelper">
                <FormattedMessage
                    defaultMessage="Requested {date}"
                    values={{
                        date: dayjs(assessment.sentAt).format("DD MMM YYYY"),
                    }}
                />
            </Typography>
        );
    }

    // Deadline line: only for open reviews (a concluded/expired evaluation no
    // longer needs a deadline nudge). Turns amber within 3 days and red once
    // overdue so reviewers can triage at a glance.
    let deadlineLine: React.ReactNode = null;
    if (status !== "Submitted" && !assessment.expired && assessment.deadline) {
        const due = dayjs(assessment.deadline);
        const daysLeft = due.startOf("day").diff(dayjs().startOf("day"), "day");
        const color = daysLeft < 0 ? "error.main" : daysLeft <= 3 ? "warning.main" : "textTextHelper";
        deadlineLine = (
            <Typography variant="textSm" color={color} fontWeight={daysLeft <= 3 ? "medium" : undefined}>
                {daysLeft < 0 ? (
                    <FormattedMessage
                        defaultMessage="Overdue (was due {date})"
                        values={{ date: due.format("DD MMM YYYY") }}
                    />
                ) : daysLeft === 0 ? (
                    <FormattedMessage defaultMessage="Due today" />
                ) : (
                    <FormattedMessage
                        defaultMessage="Due in {daysLeft, plural, one {# day} other {# days}} ({date})"
                        values={{ daysLeft, date: due.format("DD MMM YYYY") }}
                    />
                )}
            </Typography>
        );
    }

    const metadata: React.ReactNode =
        dateLine || deadlineLine ? (
            <Stack spacing={0.25}>
                {dateLine}
                {deadlineLine}
            </Stack>
        ) : null;

    const button: React.ReactNode =
        assessment.expired && status !== "Submitted" ? null : status === "Submitted" ? (
            <Button
                size="small"
                color="secondary"
                component={Link}
                to={{
                    pathname: `${basePath}/${assessment.id}/`,
                    search: `?${searchParams.toString()}`,
                }}
                sx={{ whiteSpace: "nowrap" }}
            >
                {assessment.editable ? (
                    <FormattedMessage defaultMessage="Edit feedback" />
                ) : (
                    <FormattedMessage defaultMessage="View feedback" />
                )}
            </Button>
        ) : (
            <Button
                size="small"
                component={Link}
                to={{
                    pathname: `${basePath}/${assessment.id}/`,
                    search: `?${searchParams.toString()}`,
                }}
                sx={{ whiteSpace: "nowrap" }}
            >
                <FormattedMessage defaultMessage="Start review" />
            </Button>
        );

    return (
        <ListItem sx={{ width: "100%" }} disableGutters>
            <Card sx={{ width: "100%" }}>
                <CardContent sx={{ overflow: "hidden" }}>
                    <Grid
                        container
                        direction="row"
                        justifyContent={{ xs: "space-between", sm: "flex-start" }}
                        alignItems="center"
                        gap={1}
                    >
                        <Grid size={{ xs: 12, sm: 5 }}>
                            <Stack spacing={0.5} minWidth={0}>
                                <Typography
                                    variant="textSm"
                                    fontWeight="medium"
                                    sx={{ wordBreak: "break-word", overflowWrap: "break-word" }}
                                >
                                    {assessment.supplierName}
                                </Typography>
                                <Typography variant="textSm" color="text.secondary">
                                    {assessment.questionnaire.name}
                                </Typography>
                                {showScope && (
                                    <Stack direction="row" gap={0.5} flexWrap="wrap" pt={0.5}>
                                        {spendCategoryName && (
                                            <Chip
                                                size="small"
                                                color="category"
                                                variant="status"
                                                icon={<Share />}
                                                label={spendCategoryName}
                                            />
                                        )}
                                        {businessUnitName && (
                                            <Chip
                                                size="small"
                                                color="businessUnit"
                                                variant="status"
                                                icon={<Company />}
                                                label={businessUnitName}
                                            />
                                        )}
                                    </Stack>
                                )}
                            </Stack>
                        </Grid>
                        <Grid size={{ xs: "auto", sm: "grow" }}>{metadata}</Grid>
                        <Grid size={{ xs: "auto" }}>{chip}</Grid>
                        <Grid size={{ xs: "auto" }}>{button}</Grid>
                    </Grid>
                </CardContent>
            </Card>
        </ListItem>
    );
};
